import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useLanguage } from '../context/LanguageContext'
import { mdToHtml } from '../components/MarkdownField'
import { tournamentApi } from '../services/api'
import { TERMS_FALLBACK_MD, localizedTerms } from '../config/termsContent'

/**
 * Public Terms of Use page (/terms). Linked from the reseller program landing
 * footer and the reseller portal footer.
 *
 * Renders the current published version's content from the API; when a version
 * has no stored content (e.g. the seeded version), it falls back to the bundled
 * copy in config/termsContent.js. The legal body is a single markdown document
 * rather than per-sentence t() keys — a contract is maintained as one whole
 * document. The page chrome IS localized.
 */
export default function Terms() {
  const { t, langId } = useLanguage()
  const [version, setVersion] = useState(null)

  useEffect(() => {
    let alive = true
    tournamentApi.get('/public/terms/current')
      .then(r => { if (alive) setVersion(r.data?.currentVersion || null) })
      .catch(() => { /* keep bundled fallback */ })
    return () => { alive = false }
  }, [])

  const contentMd = version ? localizedTerms(version, langId).content : TERMS_FALLBACK_MD
  const html = mdToHtml(contentMd)
  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', background: '#f8fafc' }}>
      <style>{`
        .legal-content { color: #334155; font-size: 15px; line-height: 1.7; }
        .legal-content h2 { font-size: 20px; color: #0f172a; margin: 32px 0 10px; }
        .legal-content p { margin: 0 0 14px; }
        .legal-content ul { margin: 0 0 16px; padding-left: 22px; }
        .legal-content li { margin: 0 0 6px; }
        .legal-content strong { color: #0f172a; }
        .legal-content a { color: var(--brand-600, #00876a); }
      `}</style>

      {/* Nav */}
      <nav style={{ background: '#fff', borderBottom: '1px solid var(--card-border, #e2e8f0)', padding: '16px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Link to="/" style={{ display: 'flex', alignItems: 'center' }}>
          <img src="/integolf-logo.svg" alt="InteGolf" style={{ height: 36 }} />
        </Link>
        <Link to="/" style={{ color: 'var(--secondary-800, #334155)', fontWeight: 600, fontSize: 14 }}>
          {t('sp_terms_back_home', 'Back to home')}
        </Link>
      </nav>

      {/* Content */}
      <main style={{ flex: 1, padding: '48px 20px' }}>
        <div style={{ maxWidth: 820, margin: '0 auto', background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: '40px 44px' }}>
          <h1 style={{ fontSize: 30, color: '#0f172a', margin: '0 0 4px' }}>{t('sp_terms_of_use', 'Terms of Use')}</h1>
          {version?.version && (
            <div style={{ fontSize: 12, color: '#94a3b8', marginBottom: 12 }}>
              {t('sp_terms_version_label', 'Version')} {version.version}
            </div>
          )}
          <div className="legal-content" dangerouslySetInnerHTML={{ __html: html }} />
        </div>
      </main>

      {/* Footer */}
      <footer style={{ padding: '24px', background: 'var(--secondary-950, #0f172a)', color: 'rgba(255,255,255,0.7)', textAlign: 'center', fontSize: 13 }}>
        &copy; {new Date().getFullYear()} InteGolf &middot; {t('sp_play_and_stay_portal', 'Stay and Play Portal')}
      </footer>
    </div>
  )
}
